import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, X } from 'lucide-react';

import { useToast } from '../../contexts/ToastContext.jsx';
import { ErrorBanner, InlineSpinner } from '../../components/common/PageState.jsx';
import {
  adminListOrgs,
  adminCreateOrg,
  adminCreateUser,
} from '../../services/api.js';

const PLAN_TIERS = [
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'enterprise', label: 'Enterprise' },
];

const STATUS_STYLES = {
  active: 'bg-verified/15 text-verified border-verified/30',
  onboarding: 'bg-nurture/15 text-nurture border-nurture/30',
  paused: 'bg-warm/15 text-warm border-warm/30',
  cancelled: 'bg-hot/15 text-hot border-hot/30',
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || 'bg-[#6b7280]/15 text-[#6b7280] border-[#6b7280]/30';
  return (
    <span
      className={`inline-flex items-center rounded-badge border px-2 py-0.5 text-xs font-medium capitalize ${cls}`}
    >
      {status || '—'}
    </span>
  );
}

function PlanPill({ plan }) {
  if (!plan) return <span className="text-text-secondary">—</span>;
  return (
    <span className="inline-flex items-center rounded-badge border border-border-card bg-bg-subtle px-2 py-0.5 text-xs font-medium capitalize text-text-primary">
      {plan}
    </span>
  );
}

function formatRelative(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';
  const diffMs = Date.now() - then.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  return `${months}mo ago`;
}

function formatNumber(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString();
}

export default function Clients() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const loadOrgs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminListOrgs();
      setOrgs(data.orgs || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load clients.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrgs();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="page-subtitle">
            Every organization on the ATLACIS Software platform.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="btn-primary inline-flex items-center gap-2 shrink-0"
        >
          <Plus size={16} />
          Create Client
        </button>
      </div>

      {loading && (
        <div className="card p-4">
          <InlineSpinner label="Loading clients…" />
        </div>
      )}

      {error && !loading && (
        <ErrorBanner message={error} detail="admin/orgs" onRetry={loadOrgs} />
      )}

      {!loading && !error && (
        <section className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-text-secondary border-b border-border-card">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Plan</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Users</th>
                  <th className="px-5 py-3 font-semibold text-right">Leads</th>
                  <th className="px-5 py-3 font-semibold text-right">Credits</th>
                  <th className="px-5 py-3 font-semibold">Last Activity</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgs.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-10 text-center text-text-secondary"
                    >
                      No client organizations yet. Create your first one.
                    </td>
                  </tr>
                )}
                {orgs.map((org) => (
                  <tr
                    key={org.id}
                    onClick={() => navigate(`/admin/clients/${org.id}`)}
                    className="border-b border-border-card/60 cursor-pointer hover:bg-bg-subtle transition-colors"
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="h-8 w-8 shrink-0 rounded-btn bg-accent/10 text-accent flex items-center justify-center">
                          <Building2 size={16} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-text-primary truncate">
                            {org.name}
                          </div>
                          <div className="text-xs text-text-secondary truncate">
                            {org.timezone || '—'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <PlanPill plan={org.plan_tier} />
                    </td>
                    <td className="px-5 py-3">
                      <StatusBadge status={org.status} />
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {formatNumber(org.users_count)}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {formatNumber(org.leads_count)}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      <span className="text-text-primary font-medium">
                        {formatNumber(org.credits_remaining)}
                      </span>
                      <span className="text-text-secondary text-xs">
                        {' '}
                        / {formatNumber(org.credits?.total ?? 0)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-text-secondary">
                      {formatRelative(org.last_activity_at)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/clients/${org.id}`);
                        }}
                        className="text-xs font-semibold text-accent hover:underline"
                      >
                        Manage →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {showCreate && (
        <CreateClientModal
          onClose={() => setShowCreate(false)}
          onCreated={(org) => {
            showToast(`Created ${org.name}`, { type: 'success' });
            setShowCreate(false);
            loadOrgs();
          }}
        />
      )}
    </div>
  );
}

function CreateClientModal({ onClose, onCreated }) {
  const { showToast } = useToast();
  const [form, setForm] = useState({
    name: '',
    plan_tier: 'growth',
    enrichment_credits_monthly: 500,
    admin_name: '',
    admin_email: '',
    admin_password: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (!form.name.trim()) {
      setError('Organization name is required.');
      return;
    }
    if (!form.admin_name.trim() || !form.admin_email.trim() || !form.admin_password) {
      setError('Admin user name, email, and password are required.');
      return;
    }
    if (form.admin_password.length < 8) {
      setError('Admin password must be at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      const orgRes = await adminCreateOrg({
        name: form.name.trim(),
        plan_tier: form.plan_tier,
        enrichment_credits_monthly: Number(form.enrichment_credits_monthly) || 0,
        status: 'active',
      });
      const org = orgRes.org;

      await adminCreateUser(org.id, {
        name: form.admin_name.trim(),
        email: form.admin_email.trim(),
        password: form.admin_password,
        role: 'team_leader',
      });

      onCreated(org);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : detail?.error || err.message || 'Failed to create client.';
      setError(msg);
      showToast(msg, { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-client-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl card p-6 space-y-5"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3">
          <div>
            <h2 id="create-client-title" className="section-header mb-1">
              Create Client
            </h2>
            <p className="text-sm text-text-secondary">
              Provision a new client organization + their first team leader.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-text-secondary hover:text-text-primary"
          >
            <X size={18} />
          </button>
        </header>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">
              Organization Name
            </label>
            <input
              className="input"
              value={form.name}
              onChange={update('name')}
              placeholder="Smith Luxury Group"
              autoFocus
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">
                Plan Tier
              </label>
              <select
                className="input"
                value={form.plan_tier}
                onChange={update('plan_tier')}
              >
                {PLAN_TIERS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">
                Monthly Credits
              </label>
              <input
                className="input"
                type="number"
                min="0"
                value={form.enrichment_credits_monthly}
                onChange={update('enrichment_credits_monthly')}
              />
            </div>
          </div>

          <div className="pt-2 border-t border-border-card">
            <div className="text-xs uppercase tracking-wider text-text-secondary mt-3 mb-2">
              Admin User
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">
                  Name
                </label>
                <input
                  className="input"
                  value={form.admin_name}
                  onChange={update('admin_name')}
                  placeholder="Marco Rivera"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">
                  Email
                </label>
                <input
                  className="input"
                  type="email"
                  value={form.admin_email}
                  onChange={update('admin_email')}
                  placeholder="marco@smithluxury.com"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">
                  Temporary Password
                </label>
                <input
                  className="input"
                  type="password"
                  value={form.admin_password}
                  onChange={update('admin_password')}
                  placeholder="At least 8 characters"
                  minLength={8}
                  required
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="text-sm text-[#dc2626] bg-[#fef2f2] border border-[#fca5a5] rounded-btn px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-outlined"
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
